import { useMutation } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ADD_KB_WEBSITE_URL } from '@/apollo/schemas/knowledgeBase/addKbWebsiteUrl';
import { UPDATE_KB_WEBSITE_URL } from '@/apollo/schemas/knowledgeBase/updateKbWebsiteUrl';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useKbAuth } from '@/hooks/useKbAuth';
import { IKbWebsiteUrl } from '@/types/knowledgeBase.types';
import { isKbOk, kbErrorMessage, validateKbUrl } from '@/utils/knowledgeBase';

interface KbUrlDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit to add a new source; pass a row to edit its URL. */
  editing?: IKbWebsiteUrl | null;
  onSaved: () => void;
}

const KbUrlDialog = ({
  open,
  onOpenChange,
  editing,
  onSaved,
}: KbUrlDialogProps) => {
  const { toast } = useToast();
  const { kbContext } = useKbAuth();

  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(editing);

  useEffect(() => {
    if (!open) return;
    setUrl(editing?.url ?? '');
    setError(null);
  }, [open, editing]);

  const handleFailure = (title: string, description: string) => {
    setError(description);
    toast({ variant: 'destructive', title, description });
  };

  const handleSuccess = (description: string) => {
    toast({ variant: 'success', title: 'Knowledge source saved', description });
    onSaved();
    onOpenChange(false);
  };

  const [addUrl, { loading: adding }] = useMutation(ADD_KB_WEBSITE_URL, {
    onCompleted: (data) => {
      const result = data?.addKbWebsiteUrl;
      if (!isKbOk(result)) {
        handleFailure(
          'Could not add source',
          kbErrorMessage(result, 'The URL could not be added.'),
        );
        return;
      }
      handleSuccess(
        `${result.url?.url ?? url} was added. Run a sync to crawl it.`,
      );
    },
    onError: (mutationError) =>
      handleFailure('Could not add source', mutationError.message),
  });

  const [updateUrl, { loading: updating }] = useMutation(
    UPDATE_KB_WEBSITE_URL,
    {
      onCompleted: (data) => {
        const result = data?.updateKbWebsiteUrl;
        if (!isKbOk(result)) {
          handleFailure(
            'Could not update source',
            kbErrorMessage(result, 'The URL could not be updated.'),
          );
          return;
        }
        handleSuccess(
          `${result.url?.url ?? url} was updated. Run a sync to re-crawl it.`,
        );
      },
      onError: (mutationError) =>
        handleFailure('Could not update source', mutationError.message),
    },
  );

  const loading = adding || updating;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const validationError = validateKbUrl(url);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    const trimmed = url.trim();

    if (editing) {
      if (trimmed === editing.url) {
        onOpenChange(false);
        return;
      }
      // Only the changed field is sent — `id` on its own is a 400.
      updateUrl({
        variables: { input: { id: editing.id, url: trimmed } },
        context: kbContext(),
      });
      return;
    }

    addUrl({
      variables: { input: { url: trimmed } },
      context: kbContext(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={loading ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-[520px] bg-white dark:bg-foreground dark:text-white">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit knowledge source' : 'Add knowledge source'}
          </DialogTitle>
          <DialogDescription>
            Pages are crawled for text and fed to the assistant&apos;s knowledge
            base. Changes take effect after the next sync.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="kb-url">Page URL</Label>
            <Input
              id="kb-url"
              autoFocus
              value={url}
              placeholder="https://example.com/about"
              onChange={(event) => setUrl(event.target.value)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'kb-url-error' : undefined}
            />
            {error && (
              <p id="kb-url-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={loading}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Save changes' : 'Add source'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default KbUrlDialog;
